import i18next from 'i18next';
import { STRUCTURE_EDITOR_BBX_THRESHOLDS } from '../../../../shared/constants.js';
import '../../../../i18n.js';
import { getTaskTypeLabel, TaskType } from '../../task-types/task-main.js';

interface StructureNode {
  taskNumber?: string;
  taskType?: string;
  subtasks?: StructureNode[];
  descriptionWarningCodes?: string[];
  descriptionBoundingBoxes?: Array<{ confidence?: number }>;
  maxPoints?: number;
}

const WARNING_LABELS: Record<string, string> = {
  text_cutoff: 'textCutoff',
  missing_information: 'missingInformation',
  image_unreadable: 'imageUnreadable',
};

function normalizeTaskNumber(value: unknown, language = 'de'): string {
  const translate = (key: string, options?: { defaultValue?: string }) => i18next.t(key, { ...options, lng: language });
  value = Object.values(TaskType).includes(value as TaskType) ? getTaskTypeLabel(value as TaskType, translate) : value;
  return String(value ?? '')
    .trim()
    .replace(/^Aufgabe\s+/, '')
    .replace(/^Task\s+/, '');
}

export function formatStructureForDisplay(nestedTasks: StructureNode[], language = 'de'): string {
  const tasks = Array.isArray(nestedTasks) ? nestedTasks : [];
  const roots = tasks.filter(task => normalizeTaskNumber(task?.taskNumber, language).length > 0);

  const preview: Array<{ indent: number; label: string; isRoot: boolean; taskType: string; warningCodes: string[] }> =
    [];
  const warningLabels = new Set<string>();
  const lowConfidenceTaskLabels = new Set<string>();
  let totalCount = 0;
  let hasPoints = false;

  const walk = (task: StructureNode, depth: number, parentPath: string[] = []) => {
    const taskNumber = normalizeTaskNumber(task?.taskNumber, language);
    if (!taskNumber) return;

    const taskPath = [...parentPath, taskNumber];
    totalCount += 1;
    if (typeof task?.maxPoints === 'number' && task.maxPoints > 0) hasPoints = true;

    const codes = Array.isArray(task.descriptionWarningCodes) ? task.descriptionWarningCodes : [];

    for (const code of codes) {
      const label = WARNING_LABELS[code] ?? code;
      if (label) warningLabels.add(label);
    }

    const hasLowConfidence = (Array.isArray(task.descriptionBoundingBoxes) ? task.descriptionBoundingBoxes : []).some(
      box => Number(box?.confidence ?? 1) < STRUCTURE_EDITOR_BBX_THRESHOLDS.HIGH_CONFIDENCE,
    );
    if (hasLowConfidence) {
      const root = taskPath[0];
      const rest = taskPath.slice(1);
      const taskLabel = rest.length > 0 ? `${root}.${rest.join('.')}` : `${root}`;
      lowConfidenceTaskLabels.add(taskLabel);
    }

    preview.push({
      indent: depth,
      label: taskNumber,
      isRoot: depth === 0,
      taskType: task?.taskType ?? '',
      warningCodes: codes.map(c => WARNING_LABELS[c] ?? c),
    });

    const subtasks = Array.isArray(task?.subtasks) ? task.subtasks : [];
    for (const subtask of subtasks) {
      walk(subtask, depth + 1, taskPath);
    }
  };

  for (const rootTask of roots) {
    walk(rootTask, 0);
  }

  const mainTaskCount = roots.length;
  const subTaskCount = Math.max(0, totalCount - mainTaskCount);

  const payload = {
    mainTaskCount,
    subTaskCount,
    preview,
    warningLabels: Array.from(warningLabels),
    lowConfidenceTaskLabels: Array.from(lowConfidenceTaskLabels),
    hasPoints,
  };

  return `[[STRUCTURE_CONFIRMATION]]${JSON.stringify(payload)}`;
}
